import React, { useState, useEffect } from 'react';
import { TrendingUp, ArrowUpRight, ArrowDownRight, Edit2, Users, DollarSign, LineChart, Archive } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface RevenueMetric {
  id: string;
  label: string;
  current: number;
  target: number;
  trend: number;
  type: 'revenue' | 'subscribers' | 'growth';
  period: 'weekly' | 'monthly' | 'quarterly' | 'annual';
  targetDate: Date;
}

export function RevenueGoals() {
  const [metrics, setMetrics] = useState<RevenueMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingMetric, setEditingMetric] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchMetrics();
    
    // Set up realtime subscription
    const subscription = supabase
      .channel('revenue_metrics_changes')
      .on('postgres_changes', 
        {
          event: '*',
          schema: 'public',
          table: 'revenue_metrics'
        },
        () => {
          fetchMetrics();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('revenue_metrics')
        .select('*')
        .eq('user_id', user.id)
        .eq('archived', false)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setMetrics(data?.map(metric => ({
        id: metric.id,
        label: metric.label,
        current: metric.current,
        target: metric.target,
        trend: metric.trend,
        type: metric.type,
        period: metric.period,
        targetDate: new Date(metric.target_date)
      })) || []);
    } catch (err) {
      console.error('Error fetching revenue metrics:', err);
      setError('Failed to load revenue metrics');
    } finally {
      setLoading(false);
    }
  };

  const getMetricIcon = (type: RevenueMetric['type']) => {
    switch (type) {
      case 'subscribers':
        return <Users className="w-4 h-4" />;
      case 'revenue':
        return <DollarSign className="w-4 h-4" />;
      case 'growth':
        return <LineChart className="w-4 h-4" />;
    }
  };

  const handleUpdateMetric = async (id: string, updates: Partial<RevenueMetric>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('revenue_metrics')
        .update({
          label: updates.label,
          current: updates.current,
          target: updates.target,
          target_date: updates.targetDate?.toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      await fetchMetrics();
      setEditingMetric(null);
    } catch (err) {
      console.error('Error updating metric:', err);
      setError('Failed to update metric');
    }
  };

  const handleArchiveMetric = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('revenue_metrics')
        .update({ archived: true })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      await fetchMetrics();
    } catch (err) {
      console.error('Error archiving metric:', err);
      setError('Failed to archive metric');
    }
  };

  const handleAddMetric = async (metric: Omit<RevenueMetric, 'id'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('revenue_metrics')
        .insert({
          user_id: user.id,
          label: metric.label,
          current: metric.current,
          target: metric.target,
          trend: metric.trend,
          type: metric.type,
          period: metric.period,
          target_date: metric.targetDate.toISOString()
        });

      if (error) throw error;
      await fetchMetrics();
    } catch (err) {
      console.error('Error adding metric:', err);
      setError('Failed to add metric');
    }
  };

  return (
    <div className="bg-[#141414] rounded-3xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--accent-color)]/10 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-[var(--accent-color)]" />
          </div>
          <h2 className="text-lg font-medium text-white">Revenue Goals</h2>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-[var(--accent-color)] text-black rounded-xl hover:opacity-90 transition-opacity"
        >
          Add Metric
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-xl mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-[var(--accent-color)] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {metrics.map((metric) => (
            <div key={metric.id} className="p-4 bg-[#2A2B2E] rounded-2xl hover:ring-1 hover:ring-white/10 transition-all">
              {editingMetric === metric.id ? (
                <form 
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    handleUpdateMetric(metric.id, {
                      label: formData.get('label') as string,
                      current: Number(formData.get('current')),
                      target: Number(formData.get('target')),
                      targetDate: new Date(formData.get('targetDate') as string)
                    });
                  }}
                  className="space-y-3"
                >
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Title</label>
                    <input
                      type="text"
                      name="label"
                      defaultValue={metric.label}
                      className="w-full px-4 py-2 bg-black/20 rounded-lg text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Current</label>
                    <input
                      type="number"
                      name="current"
                      defaultValue={metric.current}
                      className="w-full px-4 py-2 bg-black/20 rounded-lg text-white"
                      min={0}
                      step={metric.type === 'subscribers' ? 1 : 100}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Target</label>
                    <input
                      type="number"
                      name="target"
                      defaultValue={metric.target}
                      className="w-full px-4 py-2 bg-black/20 rounded-lg text-white"
                      min={0}
                      step={metric.type === 'subscribers' ? 1 : 100}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Target Date</label>
                    <input
                      type="date"
                      name="targetDate"
                      defaultValue={metric.targetDate.toISOString().split('T')[0]}
                      className="w-full px-4 py-2 bg-black/20 rounded-lg text-white"
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-[var(--accent-color)] text-black rounded-lg hover:opacity-90 transition-opacity"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingMetric(null)}
                      className="flex-1 px-4 py-2 bg-black/20 text-white rounded-lg hover:bg-black/30 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getMetricIcon(metric.type)}
                      <span className="text-sm text-white/70">{metric.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingMetric(metric.id)}
                        className="p-2 hover:bg-black/20 rounded-lg transition-colors text-white"
                      >
                        <Edit2 className="w-4 h-4 text-white" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Are you sure you want to archive this metric?')) {
                            handleArchiveMetric(metric.id);
                          }
                        }}
                        className="p-2 hover:bg-black/20 rounded-lg transition-colors text-white"
                      >
                        <Archive className="w-4 h-4 text-white" />
                      </button>
                      <div className={`flex items-center gap-1 text-sm ${
                        metric.trend > 0 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {metric.trend > 0 ? (
                          <ArrowUpRight className="w-4 h-4" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4" />
                        )}
                        {Math.abs(metric.trend)}%
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-xl font-semibold text-white mb-2">
                    {metric.type === 'subscribers' ? (
                      metric.current.toLocaleString()
                    ) : (
                      `$${metric.current.toLocaleString()}`
                    )}
                  </div>

                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`absolute inset-y-0 left-0 rounded-full transition-all ${
                        'bg-[var(--accent-color)]'
                      }`}
                      style={{ width: `${(metric.current / metric.target) * 100}%` }}
                    />
                  </div>

                  <div className="mt-2 text-sm text-white">
                    Target: {metric.type === 'subscribers' ? (
                      metric.target.toLocaleString()
                    ) : (
                      `$${metric.target.toLocaleString()}`
                    )} by {metric.targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Metric Form */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-6">Add New Metric</h3>
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                handleAddMetric({
                  label: formData.get('label') as string,
                  current: Number(formData.get('current')),
                  target: Number(formData.get('target')),
                  trend: Number(formData.get('trend')),
                  type: formData.get('type') as RevenueMetric['type'],
                  period: formData.get('period') as RevenueMetric['period'],
                  targetDate: new Date(formData.get('targetDate') as string)
                });
                setShowAddForm(false);
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm text-gray-400 mb-1">Title</label>
                <input
                  type="text"
                  name="label"
                  required
                  className="w-full px-4 py-2 bg-black/20 rounded-lg text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Current</label>
                  <input
                    type="number"
                    name="current"
                    required
                    min="0"
                    className="w-full px-4 py-2 bg-black/20 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Target</label>
                  <input
                    type="number"
                    name="target"
                    required
                    min="0"
                    className="w-full px-4 py-2 bg-black/20 rounded-lg text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Trend (%)</label>
                <input
                  type="number"
                  name="trend"
                  required
                  className="w-full px-4 py-2 bg-black/20 rounded-lg text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <select
                  name="type"
                  required
                  className="w-full px-4 py-2 bg-black/20 rounded-lg text-white [&>option]:bg-[#2A2B2E]"
                >
                  <option value="revenue">Revenue</option>
                  <option value="subscribers">Subscribers</option>
                  <option value="growth">Growth</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Period</label>
                <select
                  name="period"
                  required
                  className="w-full px-4 py-2 bg-black/20 rounded-lg text-white [&>option]:bg-[#2A2B2E]"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Target Date</label>
                <input
                  type="date"
                  name="targetDate"
                  required
                  className="w-full px-4 py-2 bg-black/20 rounded-lg text-white"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[var(--accent-color)] text-black rounded-lg hover:opacity-90 transition-opacity"
                >
                  Add Metric
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 px-4 py-2 bg-black/20 text-white rounded-lg hover:bg-black/30 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}